import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { AuthService } from '../services/authService';
import { useAuthUser } from '../stores/auth';
import { telemetry } from '../services/telemetryService';

/**
 * Custom hook for authentication state and operations.
 *
 * Authenticated means BOTH an MSAL account (needed for Microsoft
 * Graph/SharePoint calls) AND an app-session user (the httpOnly cookie's
 * local counterpart, #469). Losing either one — logout, 401 bounce — flips
 * isAuthenticated to false and ProtectedRoute returns the user to /login.
 */
export const useAuth = () => {
  const { accounts, inProgress } = useMsal();
  const appUser = useAuthUser();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // MSAL restores the cached account asynchronously. Reacting to the
    // first (empty) accounts emission — before initialize() finishes —
    // reported "no session" and bounced a signed-in user to /login on
    // every cold load, forcing the Microsoft click despite a valid 15-day
    // cookie (device-test 2026-09-14). Startup is MSAL's pre-init phase;
    // hold the loading state until it clears, THEN resolve the account.
    if (inProgress === InteractionStatus.Startup) return;

    const activeAccount = AuthService.getActiveAccount();
    setAccount(activeAccount);
    setIsLoading(false);
    if (activeAccount) {
      telemetry.setAuthenticatedUser(
        activeAccount.localAccountId,
        activeAccount.tenantId,
        {
          name: appUser?.name ?? activeAccount.name ?? undefined,
          email: appUser?.email ?? activeAccount.username,
        },
      );
    } else {
      telemetry.clearAuthenticatedUser();
    }
  }, [accounts, appUser, inProgress]);

  const isAuthenticated = account !== null && appUser !== null;

  const signOut = async () => {
    try {
      setIsLoading(true);
      await AuthService.signOut();
      telemetry.clearAuthenticatedUser();
      setAccount(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = async (scopes?: string[]) => {
    return await AuthService.getAccessToken(scopes);
  };

  return {
    account,
    appUser,
    isAuthenticated,
    isLoading,
    signOut,
    getAccessToken,
  };
};

export default useAuth;
